import { useForm, Controller, type FieldValues } from 'react-hook-form';
import type { FormConfig, FormFieldConfig } from '@devfactory/core';

// =============================================================================
// FORM RENDERER
// =============================================================================

interface FormRendererProps {
  config: FormConfig;
  defaultValues?: Record<string, unknown>;
  onSubmit?: (data: FieldValues) => void;
}

export function FormRenderer({ config, defaultValues = {}, onSubmit }: FormRendererProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
  });

  const watchedValues = watch();

  const handleFormSubmit = (data: FieldValues) => {
    if (onSubmit) {
      onSubmit(data);
    } else {
      console.log('Form submitted:', data);
    }
  };

  const shouldShowField = (field: FormFieldConfig): boolean => {
    if (!field.conditional) return true;

    const { field: condField, operator, value } = field.conditional;
    const currentValue = watchedValues[condField];

    switch (operator) {
      case 'equals':
        return currentValue === value;
      case 'notEquals':
        return currentValue !== value;
      case 'contains':
        return String(currentValue).includes(String(value));
      case 'isEmpty':
        return !currentValue;
      case 'isNotEmpty':
        return !!currentValue;
      default:
        return true;
    }
  };

  const layoutClasses: Record<string, string> = {
    single: 'space-y-6',
    'two-column': 'grid grid-cols-1 md:grid-cols-2 gap-6',
    wizard: 'space-y-6',
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="w-full">
      <div className={layoutClasses[config.layout] || 'space-y-6'}>
        {config.fields.filter(shouldShowField).map((field: FormFieldConfig) => (
          <FieldRenderer
            key={field.name}
            field={field}
            control={control}
            error={(errors[field.name]?.message as string) || undefined}
          />
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// FIELD RENDERER
// =============================================================================

interface FieldRendererProps {
  field: FormFieldConfig;
  control: ReturnType<typeof useForm>['control'];
  error?: string | undefined;
}

function FieldRenderer({ field, control, error }: FieldRendererProps) {
  const baseInputClasses =
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white';
  const errorInputClasses = 'border-red-300 focus:border-red-500 focus:ring-red-500';

  return (
    <div>
      <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="mt-1">
        <Controller
          name={field.name}
          control={control}
          rules={{
            required: field.required ? `${field.label} is required` : false,
            ...getValidationRules(field),
          }}
          render={({ field: controllerField }) => {
            switch (field.type) {
              case 'text':
              case 'email':
              case 'password':
              case 'tel':
              case 'url':
                return (
                  <input
                    {...controllerField}
                    type={field.type}
                    id={field.name}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );

              case 'number':
                return (
                  <input
                    {...controllerField}
                    type="number"
                    id={field.name}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                    onChange={(e) => controllerField.onChange(e.target.valueAsNumber)}
                  />
                );

              case 'textarea':
                return (
                  <textarea
                    {...controllerField}
                    id={field.name}
                    rows={4}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );

              case 'select':
                return (
                  <select
                    {...controllerField}
                    id={field.name}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  >
                    <option value="">{field.placeholder || 'Select...'}</option>
                    {getFieldOptions(field).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                );

              case 'checkbox':
                return (
                  <div className="flex items-center">
                    <input
                      {...controllerField}
                      type="checkbox"
                      id={field.name}
                      checked={controllerField.value}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      {field.placeholder}
                    </span>
                  </div>
                );

              case 'radio':
                return (
                  <div className="space-y-2">
                    {getFieldOptions(field).map((option) => (
                      <div key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          id={`${field.name}-${option.value}`}
                          value={option.value}
                          checked={controllerField.value === option.value}
                          onChange={() => controllerField.onChange(option.value)}
                          className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label
                          htmlFor={`${field.name}-${option.value}`}
                          className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                );

              case 'date':
                return (
                  <input
                    {...controllerField}
                    type="date"
                    id={field.name}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );

              case 'datetime':
                return (
                  <input
                    {...controllerField}
                    type="datetime-local"
                    id={field.name}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );

              case 'time':
                return (
                  <input
                    {...controllerField}
                    type="time"
                    id={field.name}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );

              case 'file':
                return (
                  <input
                    type="file"
                    id={field.name}
                    onChange={(e) => controllerField.onChange(e.target.files)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                );

              case 'color':
                return (
                  <input
                    {...controllerField}
                    type="color"
                    id={field.name}
                    className="h-10 w-20 rounded border-gray-300"
                  />
                );

              case 'range':
                return (
                  <div className="flex items-center gap-4">
                    <input
                      {...controllerField}
                      type="range"
                      id={field.name}
                      min={(field.validation?.min as number) || 0}
                      max={(field.validation?.max as number) || 100}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 w-12">{controllerField.value}</span>
                  </div>
                );

              case 'hidden':
                return <input {...controllerField} type="hidden" />;

              default:
                return (
                  <input
                    {...controllerField}
                    type="text"
                    id={field.name}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} ${error ? errorInputClasses : ''}`}
                  />
                );
            }
          }}
        />
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// =============================================================================
// UTILITIES
// =============================================================================

function getFieldOptions(field: FormFieldConfig): Array<{ value: string; label: string }> {
  const options = field.validation?.options as Array<string | { value: string; label: string }> | undefined;

  if (!options) return [];

  return options.map((opt) => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    return opt;
  });
}

function getValidationRules(field: FormFieldConfig): Record<string, unknown> {
  const rules: Record<string, unknown> = {};

  if (field.validation) {
    if (field.validation.minLength) {
      rules.minLength = {
        value: field.validation.minLength,
        message: `Minimum ${field.validation.minLength} characters`,
      };
    }
    if (field.validation.maxLength) {
      rules.maxLength = {
        value: field.validation.maxLength,
        message: `Maximum ${field.validation.maxLength} characters`,
      };
    }
    if (field.validation.min !== undefined) {
      rules.min = {
        value: field.validation.min,
        message: `Minimum value is ${field.validation.min}`,
      };
    }
    if (field.validation.max !== undefined) {
      rules.max = {
        value: field.validation.max,
        message: `Maximum value is ${field.validation.max}`,
      };
    }
    if (field.validation.pattern) {
      rules.pattern = {
        value: new RegExp(field.validation.pattern as string),
        message: (field.validation.patternMessage as string) || 'Invalid format',
      };
    }
  }

  if (field.type === 'email') {
    rules.pattern = {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    };
  }

  return rules;
}
